import {
  createStyles,
  IconButton,
  makeStyles,
  SvgIconProps,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { TreeItem, TreeItemProps } from '@material-ui/lab';
import React, { useEffect, useRef, useState } from 'react';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { BimTreeNode, FileNode, IProject } from '../../api-client/autogenerated';
import { getBIMTreeNode } from '../../models/api/filesystem';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOffIcon from '../icons/FolderOff-icon';
import CircularLoader from '../loader/CircularLoader';
import { generateUniqueId } from '../../scripts/utils';
import { ascendingComparator } from '../document-index/DocumentIndexUtils';
import { GreenCheck } from '../custom-components/CustomCheckboxes';
import { useDispatch, useSelector } from 'react-redux';
import { getProjectState } from '../../features/project/selectors';
import { patchProject } from '../../models/api/project';
import { updateProject } from '../../features/project/actions';
import { getUserState } from '../../features/user/selectors';
import { useRenameContext } from './context/RenameContext';
import { isRenameJobRunning } from './context/utils/renameJobHelpers';
import { addSnackbar } from '../../features/snackbar/actions';
import { clsx } from 'clsx';

type StyledTreeItemProps = TreeItemProps & {
  id: string;
  bgColor?: string;
  color?: string;
  labelIcon: React.ElementType<SvgIconProps>;
  labelInfo?: string;
  hasChildren?: boolean;
  fileNode?: FileNode;
  parentNode?: FileNode;
  bimTreeNode?: BimTreeNode;
  BIM?: boolean;
  projectId?: string;
  labelText: string;
  callback: (a: any) => void;
  setActiveFolder: (fileNode: FileNode | undefined) => void;
  setActiveFolderSubfolders: (folders: any) => void;
  updateFolderName?: (name: string, file: FileNode) => void;
  setTableIsLoading?: React.Dispatch<any>;
  deleteFolder?: (file: FileNode) => void;
  forceRefresh: number;
  updateFileNode?: (node: FileNode) => void;
  setSelectedNode?: React.Dispatch<React.SetStateAction<string>>;
  openCreateFolderDialog?: () => void;
  openUploadFileDialog?: () => void;
  isRoot?: boolean;
  handleDownload?: () => Promise<void>;
  findParentFolder: (fullKey: string) => FileNode | undefined;
  manageParentFolder: (fullKey: string) => void;
  selectedNodes: FileNode[];
  isRecycleBin?: boolean;
  onOpenFileOptions?: (file: FileNode, anchor: HTMLElement) => void;
  onRegisterRenameOpener?: (fullKey: string, opener: () => void) => void;
  onUnregisterRenameOpener?: (fullKey: string) => void;
};

const useTreeItemStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '&$selected > $content $label, &$selected:focus > $content $label, &$selected:hover > $content $label': {
        backgroundColor: '#E6EEFF',
      },
      '&:focus > $content $label': {
        backgroundColor: 'inherit',
      },
      minHeight: 48,
      position: 'relative',
    },
    rootRecycleBin: {
      '&$selected > $content $label, &$selected:focus > $content $label, &$selected:hover > $content $label': {
        backgroundColor: '#E6EEFF',
      },
      '&:focus > $content $label': {
        backgroundColor: 'inherit',
      },
      minHeight: 48,
      position: 'absolute',
      bottom: 0,
      width: '100%',
    },
    content: {
      transition: '0.1s',
      paddingLeft: 8,
      color: theme.palette.text.secondary,
      height: 48,
      fontWeight: theme.typography.fontWeightMedium,
      '$expanded > &': {
        fontWeight: theme.typography.fontWeightRegular,
      },
      '$selected > &:hover, &:hover, &:focus > &, $selected > &': {
        backgroundColor: '#E6EEFF',
      },
    },
    group: {
      marginLeft: 16,
      /*
        '& $content': {
          paddingLeft: theme.spacing(2),
        },
        */
    },
    expanded: {
      backgroundColor: 'inherit',
    },
    selected: {},
    label: {
      transition: '0.1s',
      '&:hover': {
        backgroundColor: '#E6EEFF',
      },
      height: '100%',
      fontWeight: 'inherit',
      color: 'inherit',
    },
    labelRoot: {
      height: '100%',
      display: 'flex',
      paddingRight: 8,
      alignItems: 'center',
      padding: theme.spacing(0.5, 0),
      position: 'relative',
    },
    labelIcon: {
      marginRight: theme.spacing(1),
    },
    labelText: {
      fontWeight: 'inherit',
      flexGrow: 1,
      maxWidth: '181px',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    iconContainer: {
      width: '10px',
    },
    renaming: {
      opacity: 0.5,
      cursor: 'not-allowed',
      filter: 'grayscale(100%)',
      '& *': {
        cursor: 'not-allowed',
      },
    },
  }),
);

export default function StyledTreeItem(props: StyledTreeItemProps) {
  const classes = useTreeItemStyles();

  const {
    id,
    labelText,
    labelIcon: LabelIcon,
    labelInfo,
    color = 'black',
    bgColor = 'white',
    hasChildren,
    callback,
    setActiveFolder,
    setActiveFolderSubfolders,
    fileNode,
    parentNode,
    forceRefresh,
    bimTreeNode,
    BIM,
    projectId,
    setTableIsLoading,
    updateFolderName = () => {},
    deleteFolder = () => {},
    updateFileNode,
    setSelectedNode = () => {},
    openCreateFolderDialog = () => {},
    openUploadFileDialog = () => {},
    isRoot,
    handleDownload,
    findParentFolder,
    manageParentFolder,
    selectedNodes,
    isRecycleBin,
    onOpenFileOptions,
    onRegisterRenameOpener,
    onUnregisterRenameOpener,
    ...other
  } = props;

  const user = useSelector(getUserState);
  const project = useSelector(getProjectState)!;
  const dispatch = useDispatch();
  const [localFileNode, setLocalFileNode] = useState(fileNode);

  useEffect(() => {
    const handleCtxMenuGlobal = () => setOptionsAnchor(null);
    document.addEventListener('contextmenu', handleCtxMenuGlobal);

    return () => {
      document.removeEventListener('contextmenu', handleCtxMenuGlobal);
    };
  }, []);

  useEffect(() => {
    if (fileNode) setLocalFileNode(fileNode);
  }, [fileNode]);

  const defaultContent = hasChildren ? (
    <div style={{ paddingTop: 12, paddingBottom: 4, overflow: 'hidden', paddingRight: 16 }}>
      <CircularLoader />
    </div>
  ) : (
    <div />
  );

  const [subfolders, setSubfolders] = useState<JSX.Element[] | JSX.Element | null>(defaultContent);
  const [bimSubfoldersLoaded, setBimSubfoldersLoaded] = useState(false);
  const [optionsAnchor, setOptionsAnchor] = useState<null | HTMLElement>(null);
  const rightClickAnchor = useRef<HTMLDivElement | null>(null);
  const [editing, setEditing] = useState<boolean>(false);
  const inputRef = useRef<HTMLElement | null>(null);

  const { state: renameState } = useRenameContext();

  const normalizeKey = (k: string) => (k.endsWith('/') ? k : `${k}/`);
  const selfKey = normalizeKey(fileNode?.fullKey ?? '');
  const exactRenameJob = renameState[selfKey]; // only a job whose key == this folder's key
  const isRenameRunning = isRenameJobRunning(exactRenameJob);

  // Refresh local node once this folder's rename finishes so the new name shows
  useEffect(() => {
    if (!fileNode) return;
    const job = renameState[selfKey];
    if (!isRenameJobRunning(job)) {
      // Force a new reference even if parent mutated in place
      setLocalFileNode({ ...fileNode });
    }
  }, [
    fileNode,
    selfKey,
    renameState[selfKey]?.status, //exact folder job
    renameState[parentNode?.fullKey ?? '']?.status, // watch parent changes,
  ]);

  // Any running rename (file or folder) under this folder?
  const descendantRunningJob =
    selfKey === '/'
      ? undefined
      : Object.entries(renameState).find(([k, job]) => {
          if (!isRenameJobRunning(job)) return false;
          const rk = normalizeKey(k);
          return rk.startsWith(selfKey) && rk !== selfKey; // true for any deeper descendant
        })?.[1];

  const showFolderOff = isRenameRunning || !!descendantRunningJob; // gray out and disable when self or any descendant is renaming

  useEffect(() => {
    if (!fileNode || isRecycleBin) return;

    const key = fileNode.fullKey.replace(/\/$/, ''); // strips trailing slash if present
    const opener = () => setEditing(true);

    onRegisterRenameOpener?.(key, opener);
    return () => {
      onUnregisterRenameOpener?.(key);
    };
  }, [fileNode, editing, isRecycleBin, onRegisterRenameOpener, onUnregisterRenameOpener]);

  const handleBIMExpand = () => {
    if (BIM && !bimSubfoldersLoaded) {
      getBIMTreeNode(projectId!, bimTreeNode!)
        .then((treeNodes) => {
          if (Array.isArray(treeNodes)) {
            const formattedData = treeNodes.filter((a: BimTreeNode) => a.type === 'FOLDER');

            const subfoldersTemp = formattedData.map((folder: BimTreeNode) => {
              return (
                <StyledTreeItem
                  key={folder.id}
                  id={folder.id}
                  nodeId={folder.id}
                  labelText={folder.name}
                  hasChildren={folder.type === 'FOLDER'}
                  labelIcon={FolderIcon}
                  callback={callback}
                  setActiveFolder={setActiveFolder}
                  bimTreeNode={folder}
                  setActiveFolderSubfolders={setActiveFolderSubfolders}
                  forceRefresh={forceRefresh}
                  setTableIsLoading={setTableIsLoading}
                  updateFolderName={updateFolderName}
                  projectId={projectId}
                  setSelectedNode={setSelectedNode}
                  openCreateFolderDialog={openCreateFolderDialog}
                  openUploadFileDialog={openUploadFileDialog}
                  BIM
                  handleDownload={handleDownload}
                  findParentFolder={findParentFolder}
                  manageParentFolder={manageParentFolder}
                  selectedNodes={selectedNodes}
                  onOpenFileOptions={onOpenFileOptions}
                  onRegisterRenameOpener={onRegisterRenameOpener}
                  onUnregisterRenameOpener={onUnregisterRenameOpener}
                />
              );
            });
            setSubfolders(subfoldersTemp);
            setBimSubfoldersLoaded(true);
          }
        })
        .catch(() => {
          setSubfolders([]);
        });
    }
  };

  const handleOpenOptions = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setActiveFolder(localFileNode);
    if (localFileNode?.fullKey && onOpenFileOptions) {
      onOpenFileOptions(localFileNode, event.currentTarget);
    }
  };

  useEffect(() => {
    if (localFileNode?.children) {
      const formattedData = localFileNode!.children!.filter((a: FileNode) => a.type === 'FOLDER');
      const subfoldersTemp = formattedData
        .sort((a, b) => ascendingComparator(a, b, 'fullKey'))
        .map((folder: FileNode) => {
          return (
            <StyledTreeItem
              key={generateUniqueId()}
              id={folder.fullKey}
              nodeId={folder.fullKey + folder.ownerId}
              labelText={folder.relativeKey!}
              hasChildren={folder.type === 'FOLDER'}
              labelIcon={FolderIcon}
              callback={callback}
              setActiveFolder={setActiveFolder}
              fileNode={folder}
              parentNode={localFileNode}
              setActiveFolderSubfolders={setActiveFolderSubfolders}
              forceRefresh={forceRefresh}
              updateFolderName={updateFolderName}
              deleteFolder={deleteFolder}
              updateFileNode={updateFileNode}
              setSelectedNode={setSelectedNode}
              openCreateFolderDialog={openCreateFolderDialog}
              openUploadFileDialog={openUploadFileDialog}
              handleDownload={handleDownload}
              findParentFolder={findParentFolder}
              manageParentFolder={manageParentFolder}
              selectedNodes={selectedNodes}
              onOpenFileOptions={onOpenFileOptions}
              onRegisterRenameOpener={onRegisterRenameOpener}
              onUnregisterRenameOpener={onUnregisterRenameOpener}
            />
          );
        })
        .filter(Boolean);
      setSubfolders(subfoldersTemp as JSX.Element[]);
    }
  }, [forceRefresh, localFileNode]);

  //Function for when the label is left clicked on.
  async function handleClick(event: React.MouseEvent<Element, MouseEvent>) {
    event.preventDefault();

    if (BIM) {
      setTableIsLoading!(true);
      getBIMTreeNode(projectId!, bimTreeNode!).then((treeNodes) => {
        if (Array.isArray(treeNodes)) {
          callback(treeNodes.filter((a: BimTreeNode) => a.type == 'FILE'));
        }
      });
    } else {
      const formattedData = localFileNode!.children!;
      callback(formattedData);
    }
    setActiveFolder(localFileNode);
    setActiveFolderSubfolders(subfolders);
  }

  //Function for when any part of the tree item is clicked on (right mouse button)
  function handleContextMenu(event: any) {
    event.preventDefault();
    event.stopPropagation();

    if (BIM) {
      return;
    }
    rightClickAnchor.current!.style.top = `${event.clientY + 24}px`;
    rightClickAnchor.current!.style.left = `${event.clientX}px`;
    setOptionsAnchor(rightClickAnchor.current!);

    const formattedData = localFileNode!.children!;
    callback(formattedData);
    setActiveFolder(localFileNode);
    setActiveFolderSubfolders(subfolders);
    if (!BIM) setSelectedNode!(localFileNode!.fullKey + localFileNode!.ownerId);

    // OPEN the centralized FileOptionsDialog with the positioned div as the anchor
    if (onOpenFileOptions && localFileNode) {
      onOpenFileOptions(localFileNode, rightClickAnchor.current as HTMLElement);
    }
  }

  const handleFinishEdit = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditing(false);
    updateFolderName!(e.target.value, localFileNode!);
  };

  const keypressHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      inputRef!.current!.blur();
    }

    if (event.key === 'Escape') {
      setEditing(false);
    }

    if (event.key === '.') {
      // prevent user from tying a dot in the folder name
      event.preventDefault();
      return false;
    }
  };

  useEffect(() => {
    if (editing) {
      inputRef!.current!.focus();
    }
  }, [editing]);

  const getFolderLabel = () => {
    if (localFileNode!.fullKey === '/') return labelText;
    return labelText.replaceAll('/', '');
  };

  const handleBIMProjectSelect = async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    if (!bimTreeNode) return;
    let updatedProject = {} as IProject;
    if (!project.bimOwnerUserId) {
      updatedProject = await patchProject(project.id, {
        preferredBimProject: bimTreeNode.id,
      });
    } else if (project.bimOwnerUserId === user.id) {
      if (project.preferredBimProject === bimTreeNode.id) {
        updatedProject = await patchProject(project.id, {
          preferredBimProject: null,
        });
      } else {
        updatedProject = await patchProject(project.id, {
          preferredBimProject: bimTreeNode.id,
        });
      }
    }
    if (updatedProject.id) dispatch(updateProject(updatedProject));
  };

  const handleOptionsClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();

    // Prefer this folder's own job; otherwise use a descendant job if present
    const activeJob =
      exactRenameJob && isRenameJobRunning(exactRenameJob)
        ? exactRenameJob
        : descendantRunningJob && isRenameJobRunning(descendantRunningJob)
        ? descendantRunningJob
        : null;

    if (activeJob) {
      const percent = Math.round((activeJob as any)?.percent ?? 0);
      dispatch(
        addSnackbar({
          id: Date.now(),
          message: `Rename in progress... ${percent}% complete.`,
          severity: 'info',
        }),
      );
      return; // block opening FileOptionsDialog while renaming
    }

    handleOpenOptions(event);
  };

  return (
    <>
      {!isRoot ? (
        <TreeItem
          onContextMenu={handleContextMenu}
          label={
            //<div className={classes.labelRoot}>
            <div className={clsx(classes.labelRoot, { [classes.renaming]: showFolderOff })}>
              {/*An anchor point to open the options menu when the tree item is right clicked*/}
              <div
                style={{ position: 'fixed', top: 0, left: 0, width: 1, height: 1 }}
                ref={rightClickAnchor}
              />
              {showFolderOff ? (
                <FolderOffIcon color="inherit" className={classes.labelIcon} />
              ) : (
                <FolderIcon color="inherit" className={classes.labelIcon} />
              )}
              {editing ? (
                <TextField
                  inputRef={inputRef}
                  defaultValue={localFileNode?.relativeKey?.substr(
                    0,
                    localFileNode.relativeKey.lastIndexOf('/'),
                  )}
                  onBlur={(e) => handleFinishEdit(e)}
                  onKeyDown={(event) => keypressHandler(event)}
                  fullWidth
                  style={{ maxWidth: 250, paddingRight: 16 }}
                />
              ) : (
                <Typography variant="body2" className={classes.labelText}>
                  {BIM ? labelText : getFolderLabel()}
                  {isRenameRunning && ( //Match exact folder name so the percentage only shows for the folder being renamed
                    <span style={{ float: 'right', paddingRight: 10 }}>
                      {Math.round(exactRenameJob?.percent ?? 0)}%
                    </span>
                  )}
                </Typography>
              )}
              {!BIM && !editing && !isRecycleBin && (
                <IconButton size="small" onClick={(event) => handleOptionsClick(event)}>
                  <MoreHorizIcon />
                </IconButton>
              )}
              {BIM && (
                <GreenCheck
                  disabled={!!project.bimOwnerUserId && project.bimOwnerUserId !== user.id}
                  checked={project.preferredBimProject === bimTreeNode?.id}
                  onClick={handleBIMProjectSelect}
                  style={{ padding: 0 }}
                />
              )}
            </div>
          }
          style={{
            color,
          }}
          classes={{
            root: isRecycleBin ? classes.rootRecycleBin : classes.root,
            content: classes.content,
            expanded: classes.expanded,
            selected: classes.selected,
            group: classes.group,
            label: classes.label,
            iconContainer: classes.iconContainer,
          }}
          onIconClick={handleBIMExpand}
          onLabelClick={handleClick}
          {...other}
        >
          {hasChildren ? subfolders : null}
        </TreeItem>
      ) : (
        <>{subfolders}</>
      )}
    </>
  );
}
